/**
 * useOwnerStatistics Hook
 * 오너 통계 상태 관리 및 비즈니스 로직
 */
import { useState, useCallback, useEffect } from 'react';
import type { OverallStatistics, StatisticsPeriod } from '../models/OwnerStatistics';
import { ownerStatisticsRepository } from '../repositories/OwnerStatisticsRepository';
import { ownerStatisticsService } from '../services/OwnerStatisticsService';

/**
 * useOwnerStatistics 커스텀 훅
 */
export const useOwnerStatistics = () => {
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<OverallStatistics | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<StatisticsPeriod>(30);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  /**
   * 통계 데이터 로드
   */
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await ownerStatisticsRepository.getOverviewStatistics(selectedPeriod);
      setStatistics(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
      setError(err instanceof Error ? err.message : '통계 데이터 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  /**
   * 초기 데이터 로드 및 기간 변경 시 재로드
   */
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  /**
   * 기간 변경 핸들러
   */
  const handlePeriodChange = useCallback((period: StatisticsPeriod) => {
    setSelectedPeriod(period);
  }, []);

  /**
   * 새로고침 핸들러
   */
  const handleRefresh = useCallback(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  /**
   * 시간대별 차트 데이터
   */
  const timeSlotChartData = statistics?.time_slots
    ? ownerStatisticsService.convertTimeSlotChartData(statistics.time_slots)
    : [];

  /**
   * 요금제별 차트 데이터
   */
  const planChartData = statistics?.plans
    ? ownerStatisticsService.convertPlanChartData(statistics.plans)
    : [];

  return {
    // 상태
    loading,
    error,
    statistics,
    selectedPeriod,
    lastUpdated,
    timeSlotChartData,
    planChartData,

    // 핸들러
    handlePeriodChange,
    handleRefresh,
    fetchStatistics,
  };
};
